import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '../packages/api-client';
import { useBotProtection } from '../hooks/useBotProtection';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackAnalyticsEvent } from '../utils/analytics';

// This module is imported before Sentry initializes. Remove the token from
// the address bar before analytics/replay can record the URL. Reopening the
// email link restores it if the visitor reloads this page.
let initialToken = '';
if (window.location.pathname === '/verify-registration') {
    initialToken = new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
    window.history.replaceState(null, '', window.location.pathname);
}

export function VerifyRegistrationPage() {
    usePageTitle('Verify email and create your workspace');
    // Keep the token in memory only; GET/navigation never consumes it.
    const [token] = useState(() => initialToken || new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');
    const bot = useBotProtection();
    async function submit(event: FormEvent) {
        event.preventDefault(); setError('');
        if (password !== confirm) { setError('Passwords do not match'); return; }
        if (new TextEncoder().encode(password).length > 72) { setError('Please use a password no longer than 72 bytes.'); return; }
        setLoading(true);
        try {
            await bot.post('/auth/register/verify', { token, password });
            setDone(true); setPassword(''); setConfirm('');
            trackAnalyticsEvent('workspace_created');
            window.history.replaceState(null, '', window.location.pathname);
        } catch (error) { setError(getErrorMessage(error)); }
        finally { setLoading(false); }
    }
    return <div className="login-container"><div className="login-card">
        <h1 className="login-title">{done ? 'Your workspace is ready' : 'Choose your password'}</h1>
        {done ? <><p>Your email is verified. Sign in to start using your free workspace.</p><Link className="btn btn-primary" to="/login">Sign in</Link></> : !token ? <p>This link is missing its verification token. <Link to="/register">Request a new link</Link>.</p> : <>
            <p className="login-subtitle">Only continue if you requested a KaziAtlas workspace. Choose a password to verify your email and create it.</p>
            {error && <p role="alert">{error}</p>}
            <form className="login-form" onSubmit={submit}>
                <label htmlFor="password">Password</label><input className="form-input" id="password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={event => setPassword(event.target.value)} />
                <label htmlFor="confirm-password">Confirm password</label><input className="form-input" id="confirm-password" type="password" autoComplete="new-password" required minLength={8} value={confirm} onChange={event => setConfirm(event.target.value)} />
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating workspace…' : 'Verify email and create workspace'}</button>
                {bot.widget}
            </form>
            <p>Link expired? <Link to="/register">Request a new verification email</Link>.</p>
        </>}
    </div></div>;
}
